import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { InsuranceProductActionsCell } from 'src/components/insurance-product/grid/InsuranceProductActionsCell'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import { insuranceProductTableHeaderTitles } from './constants'
import { InsuranceProductsTableFields } from './enums'

/**
 * Wiring only — date handling comes from utils/grid, actions from InsuranceProductActionsCell.
 *
 * A factory rather than a module constant: the actions column closes over the page's edit and
 * delete handlers — and over nothing else. Both are stable `useCallback` references, so the
 * memoized result stays referentially stable for the life of the page. The id currently being
 * deleted is read by InsuranceProductActionsCell itself, precisely so this array does not have
 * to be rebuilt mid-delete: a fresh array resets the grid's column widths and order. Callers
 * must still memoize the result.
 */
export const buildInsuranceProductColumns = (
  onEditInsuranceProduct: (insuranceProduct: InsuranceProduct) => void,
  onDeleteInsuranceProduct: (insuranceProduct: InsuranceProduct) => void,
  // `insurance_products_manage` (ENG-1207 part 2). Edit and Delete both need it, so without it the
  // actions column is dropped rather than left as an empty menu.
  canManage: boolean,
): Array<GridColDef<InsuranceProduct>> => {
  const columns: Array<GridColDef<InsuranceProduct>> = [
    {
      field: InsuranceProductsTableFields.id,
      headerName: insuranceProductTableHeaderTitles[InsuranceProductsTableFields.id],
      width: 300,
      sortable: true,
      display: 'flex',
      renderCell: (cellParams: GridRenderCellParams<InsuranceProduct>) => {
        // A uuid is too long for the column, so it truncates — `title` exposes the full value
        // on hover for anyone who needs to copy it out.
        return (
          <Typography variant="body2" css={tw`truncate`} title={cellParams.row.id}>
            {cellParams.row.id}
          </Typography>
        )
      },
    },
    {
      field: InsuranceProductsTableFields.name,
      headerName: insuranceProductTableHeaderTitles[InsuranceProductsTableFields.name],
      flex: 2,
      minWidth: 200,
      sortable: true,
      // Name identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsuranceProduct>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.name}
          </Typography>
        )
      },
    },
    {
      field: InsuranceProductsTableFields.description,
      headerName: insuranceProductTableHeaderTitles[InsuranceProductsTableFields.description],
      flex: 3,
      minWidth: 200,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsuranceProduct>) => {
        const description = params.row.description?.trim()

        if (!description) {
          // A dash for a missing optional value is a cosmetic default, not fabricated content.
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {description}
          </Typography>
        )
      },
    },
    {
      field: InsuranceProductsTableFields.createdAt,
      headerName: insuranceProductTableHeaderTitles[InsuranceProductsTableFields.createdAt],
      width: 160,
      sortable: true,
      type: 'date',
      // Backend `createdAt` is unix SECONDS — parse first, then format the resulting Date.
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateOnly,
    },
    {
      field: InsuranceProductsTableFields.actions,
      headerName: insuranceProductTableHeaderTitles[InsuranceProductsTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsuranceProduct>) => {
        return (
          <InsuranceProductActionsCell
            insuranceProduct={params.row}
            onEdit={onEditInsuranceProduct}
            onDelete={onDeleteInsuranceProduct}
          />
        )
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== InsuranceProductsTableFields.actions
  })
}
